import { useQuery, useMutation } from '@apollo/client'
import {
  AgentQuery,
  AgentQueryVariables,
  DataSetQuery,
  DataSetQueryVariables,
  ArtifactsQuery,
  ArtifactsQueryVariables,
  CreateDataSetMutation,
  CreateDataSetMutationVariables,
  UpdateArtifactUuidMutationVariables,
  UpdateArtifactUuidMutation,
  CreateAgentMutation,
  CreateAgentMutationVariables,
  AddDataSetToAgentMutation,
  AddDataSetToAgentMutationVariables,
  CreateWorkflowMutation,
  CreateWorkflowMutationVariables,
  CreateAnalysisMutation,
  CreateAnalysisMutationVariables,
  CreateTaskMutation,
  CreateTaskMutationVariables,
} from 'app/javascript/components/graphql'
import {
  GET_DATA_SET_QUERY,
  CREATE_DATA_SET_MUTATION,
  ADD_DATASET_TO_AGENT_MUTATION,
} from '../../graphql/queries/data_set'
import { CREATE_AGENT_MUTATION } from '../../graphql/queries/agent'
import { CREATE_WORKFLOW_MUTATION } from '../../graphql/queries/workflow'
import { CREATE_ANALYSIS_MUTATION } from '../../graphql/queries/analysis'
import { CREATE_TASK_MUTATION } from '../../graphql/queries/task'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Step from '@mui/joy/Step'
import Accordion from '@mui/joy/Accordion'
import AccordionDetails from '@mui/joy/AccordionDetails'
import AccordionSummary from '@mui/joy/AccordionSummary'
import {
  Bell,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Maximize2,
  Minimize2,
  Bot,
  ChartArea,
  Table,
  UserCircleIcon,
  ListTodoIcon,
  Clock10Icon,
  DatabaseIcon,
  FolderOpenIcon,
  TableIcon,
  CodeIcon,
  CircleCheckIcon,
  LoaderCircleIcon,
  WrenchIcon,
  TextIcon,
  RectangleHorizontal,
  Mail,
  PaperclipIcon,
} from 'lucide-react'
import clsx from 'clsx'
import Tooltip from '@mui/joy/Tooltip'
import AccordionGroup from '@mui/joy/AccordionGroup'
import { Button } from '@headlessui/react'
import { gql } from '@apollo/client'
import { Link } from 'react-router-dom'
import { UPDATE_ARTIFACT_UUID } from '../../graphql/queries/message_thread'
import { parseCronExpression, daysOfWeek, hours } from '../../utils/cronUtils'

// GraphQL Queries
const GET_AGENT_QUERY = gql`
  query Agent($uuid: String!) {
    agent(uuid: $uuid) {
      uuid
      label
    }
  }
`

const GET_ARTIFACTS = gql`
  query Artifacts($messageThreadUuid: String!) {
    artifacts(messageThreadUuid: $messageThreadUuid) {
      uuid
      name
      description
      artifactType
      createdAt
      updatedAt
      content
    }
  }
`

// Types
type Backend = 'snowflake' | 'bigquery' | 'looker'
type StepType = 'alert' | 'analysis' | 'agent' | 'data_set' | 'email_update'

interface UuidReference {
  type: 'plan' | 'backend'
  value: string
}

interface Attachment {
  prompt: string
  file_type: 'excel' | 'csv' | 'image' | 'text'
}

interface Task {
  name: string
  description: string
  task_prompt: string
  uuid: string
  type: string
}

interface BaseStep {
  type: StepType
  name?: string
  description?: string
  uuid: UuidReference
}

interface AlertStep extends BaseStep {
  type: 'alert'
  name: string
  description: string
  agent_uuid: UuidReference
  tasks: Task[]
  cron_schedule?: string
}

interface AnalysisStep extends BaseStep {
  type: 'analysis'
  name: string
  description: string
  synthesis: string
  tasks?: Task[]
  agent_uuid: UuidReference
}

interface AgentStep extends BaseStep {
  type: 'agent'
  first_name: string
  job_title?: string
  short_description: string
  long_description: string
  system_instruction: string
  data_sets?: DataSetRef[]
}

interface EmailUpdateStep extends BaseStep {
  type: 'email_update'
  name: string
  description: string
  agent_uuid: UuidReference
  tasks?: Task[]
  synthesis?: string
  attachments?: Attachment[]
}

interface DataSetStep extends BaseStep {
  type: 'data_set'
  name: string
  description: string
  backend: Backend
  definition: TableDefinition
}

type Step = AlertStep | AnalysisStep | AgentStep | EmailUpdateStep | DataSetStep

interface DataSetRef {
  uuid: UuidReference
}

interface TableDefinition {
  type: 'snowflake' | 'bigquery'
  table_definition: {
    type: 'table' | 'view'
    schema?: string
    table_name?: string
    sql?: string
  }
}

interface StepProps {
  step: Step
  expanded: boolean
  onAccordionChange: (expanded: boolean) => void
  allSteps: Step[]
  handleArtifactChange: () => void
  artifact: Artifact
}

interface GraphQLArtifact {
  uuid: string
  name: string
  description: string
  artifactType: string
  createdAt: string
  updatedAt: string
  content: Step[]
}

interface CreateListProps {
  artifacts: GraphQLArtifact[]
  handleArtifactChange: () => void
}

interface Artifact {
  uuid: string
  name: string
  description: string
  artifactType: string
  createdAt: string
  updatedAt: string
  content: Step[]
}

function isStep(step: Step): step is Step {
  return ['alert', 'analysis', 'agent', 'email_update', 'data_set'].includes(step.type)
}

function isDataSet(step: Step): step is DataSetStep {
  return step.type === 'data_set'
}

function isAlertStep(step: Step): step is AlertStep {
  return step.type === 'alert'
}

function isAnalysisStep(step: Step): step is AnalysisStep {
  return step.type === 'analysis'
}

function isAgentStep(step: Step): step is AgentStep {
  return step.type === 'agent'
}

function isEmailUpdateStep(step: Step): step is EmailUpdateStep {
  return step.type === 'email_update'
}

// Constants
const BACKEND_LABELS: Record<Backend, string> = {
  snowflake: 'Snowflake',
  bigquery: 'BigQuery',
  looker: 'Looker',
}

const STEP_ICONS: Record<StepType, { icon: React.ReactNode; tooltip: string }> = {
  alert: { icon: <Bell size={16} className="text-gray-500" />, tooltip: 'Alert' },
  analysis: { icon: <ChartArea size={16} className="text-gray-500" />, tooltip: 'Analysis' },
  agent: { icon: <Bot size={16} className="text-gray-500" />, tooltip: 'Agent' },
  data_set: { icon: <Table size={16} className="text-gray-500" />, tooltip: 'Dataset' },
  email_update: { icon: <Mail size={16} className="text-gray-500" />, tooltip: 'Email Update' },
}

// Shared styles
const contentStyles = 'whitespace-pre-wrap break-words max-w-full overflow-x-auto'

// Helper function to convert string to UuidReference
const toUuidReference = (value: string, type: 'plan' | 'backend'): UuidReference => ({
  type,
  value,
})

// Helper function to check for plan dependencies recursively
const hasPlanDependencies = (step: Step): boolean => {
  // For DataSet type, check data_sets field
  if (isDataSet(step)) {
    return false // DataSet type doesn't have nested dependencies
  }

  if (isAgentStep(step)) {
    // Check data_sets if they exist
    if (step.data_sets) {
      return step.data_sets.some((ds) => ds.uuid.type === 'plan')
    }
  }

  if (isAlertStep(step)) {
    if (step.agent_uuid.type === 'plan') {
      return true
    }
  }

  return false
}

// Helper function to get the display ID for a step
const getStepId = (uuid: UuidReference): string => {
  return `${uuid.type}-${uuid.value}`
}

// Custom hook for getting agent name
const useAgent = (step: Step, allSteps: Step[]) => {
  const findAgentUuid = (step: Step) => {
    if (isAlertStep(step) || isAnalysisStep(step) || isEmailUpdateStep(step)) {
      // Handle both structures: direct agent_uuid and nested agent object
      const agentRef: UuidReference = (step as any).agent_uuid
      return agentRef?.value
    }
    if (isAgentStep(step)) {
      return step.uuid.value
    }
    return null
  }

  const agentUuid = findAgentUuid(step)
  const { data, loading } = useQuery<AgentQuery, AgentQueryVariables>(GET_AGENT_QUERY, {
    skip: !agentUuid,
    variables: {
      uuid: agentUuid || '',
    },
  })

  const agentName = React.useMemo(() => {
    if (loading) {
      return 'Loading...'
    }
    if (data?.agent?.label) {
      return data.agent.label
    }
    if (isAlertStep(step) || isAnalysisStep(step) || isEmailUpdateStep(step)) {
      if (step.agent_uuid.type === 'plan') {
        const agentStep = allSteps.find(
          (currentStep) =>
            isAgentStep(currentStep) && currentStep.uuid.value === step.agent_uuid.value
        ) as AgentStep | undefined
        return agentStep?.first_name || 'Unknown Agent'
      }
    }
    if (isAgentStep(step)) {
      return step.first_name || 'Unknown Agent'
    }
    return 'Unknown Agent'
  }, [data, loading, step, allSteps])

  return { agentName, loading }
}

// Custom hook for getting data set names
const useDataSets = (dataSets: DataSetRef[] | undefined, allSteps: Step[]) => {
  // Find UUIDs that need to be fetched from the backend
  const uuidsToFetch = React.useMemo(() => {
    if (!dataSets) {
      return []
    }
    return dataSets
      .filter((ds: DataSetRef) => ds.uuid.type === 'backend')
      .map((ds) => ds.uuid.value)
  }, [dataSets])

  // Create a query for each UUID that needs to be fetched
  const queries = uuidsToFetch.map((uuid) =>
    useQuery<DataSetQuery, DataSetQueryVariables>(GET_DATA_SET_QUERY, {
      variables: { uuid },
      skip: !uuid,
    })
  )

  return React.useMemo(() => {
    if (!dataSets) {
      return []
    }

    return dataSets.map((ds: DataSetRef) => {
      // If it's a new data set, look in all steps
      if (ds.uuid.type === 'plan') {
        const dataSetStep = allSteps.find(
          (currentStep) => isDataSet(currentStep) && currentStep.uuid.value === ds.uuid.value
        )
        return dataSetStep?.name || ds.uuid.value
      }

      // Otherwise, look in fetched data
      const queryIndex = uuidsToFetch.indexOf(ds.uuid.value)
      if (queryIndex === -1) {
        return ds.uuid.value
      }

      const { data, loading } = queries[queryIndex]
      if (loading) {
        return 'Loading...'
      }
      return data?.dataSet?.name || ds.uuid.value
    })
  }, [dataSets, allSteps, uuidsToFetch, queries])
}

// Shared Components
const InfoRow = ({
  icon,
  content,
  className = '',
}: {
  icon: React.ReactNode
  content: React.ReactNode
  className?: string
}) => (
  <div className={`flex w-full flex-none gap-x-4 border-t py-2 border-gray-900/5 ${className}`}>
    <dt className="flex-none">{icon}</dt>
    <dd className="text-sm/6 font-medium text-gray-900">{content}</dd>
  </div>
)

const StepHeader = ({ type, name }: { type: StepType; name: string }) => {
  const { icon, tooltip } = STEP_ICONS[type] || STEP_ICONS.alert

  return (
    <div className="flex items-center gap-2">
      <Tooltip title={tooltip} placement="top">
        <div className="p-1">{icon}</div>
      </Tooltip>
      <span className="font-medium">{name}</span>
    </div>
  )
}

const CreateButton = ({
  handleArtifactChange,
  step,
  artifact,
}: {
  handleArtifactChange: () => void
  step: Step
  artifact: Artifact
}): JSX.Element => {
  const [create, setCreate] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createDataSet] = useMutation<CreateDataSetMutation, CreateDataSetMutationVariables>(
    CREATE_DATA_SET_MUTATION
  )
  const [createAgent] = useMutation<CreateAgentMutation, CreateAgentMutationVariables>(
    CREATE_AGENT_MUTATION
  )
  const [createWorkflow] = useMutation<CreateWorkflowMutation, CreateWorkflowMutationVariables>(
    CREATE_WORKFLOW_MUTATION
  )
  const [createAnalysis] = useMutation<CreateAnalysisMutation, CreateAnalysisMutationVariables>(
    CREATE_ANALYSIS_MUTATION
  )
  const [createTask] = useMutation<CreateTaskMutation, CreateTaskMutationVariables>(
    CREATE_TASK_MUTATION
  )
  const [addDataSetToAgent] = useMutation<
    AddDataSetToAgentMutation,
    AddDataSetToAgentMutationVariables
  >(ADD_DATASET_TO_AGENT_MUTATION)
  const [updateArtifactUuid] = useMutation<
    UpdateArtifactUuidMutation,
    UpdateArtifactUuidMutationVariables
  >(UPDATE_ARTIFACT_UUID)

  const handleCreate = async () => {
    if (!isDataSet(step) && !isAgentStep(step) && !isAlertStep(step) && !isAnalysisStep(step)) {
      return
    }

    setCreateLoading(true)
    try {
      if (isAnalysisStep(step)) {
        const { data } = await createAnalysis({
          variables: {
            name: step.name,
            description: step.description,
            synthesis: step.synthesis,
            agentUuid: step.agent_uuid.value,
          },
        })

        if (data?.createAnalysis.success && data.createAnalysis.analysis) {
          // Create all defined tasks
          if (step.tasks && step.tasks.length > 0) {
            for (const taskDef of step.tasks) {
              const taskResponse = await createTask({
                variables: {
                  name: taskDef.name,
                  description: taskDef.description,
                  task: taskDef.task_prompt,
                  outputWebhookUrl: '',
                  runSchedule: '',
                  taskType: '',
                  taskableType: 'Analysis',
                  taskableUuid: data.createAnalysis.analysis.uuid,
                },
              })

              if (taskResponse.data.createTask.errors.length > 0) {
                console.error('Task creation failed:', taskResponse.data.createTask.errors)
              }
            }
          }

          setCreate(true)
          await updateArtifactUuid({
            variables: {
              artifactUuid: artifact.uuid,
              planUuid: step.uuid.value,
              backendUuid: data.createAnalysis.analysis.uuid,
            },
          })
          handleArtifactChange()
          window.toastr.success('Analysis created successfully')
        } else {
          const errors = data?.createAnalysis.errors || ['Failed to create analysis']
          console.error('Analysis creation failed:', errors)
          window.toastr.error(errors.join(', '))
        }
      } else if (isAlertStep(step)) {
        const { data } = await createWorkflow({
          variables: {
            name: step.name,
            description: step.description,
            agentUuid: step.agent_uuid.value,
            cronSchedule: step.cron_schedule,
          },
        })

        if (data?.createWorkflow.success && data.createWorkflow.workflow) {
          // Create all defined tasks
          if (step.tasks && step.tasks.length > 0) {
            for (const taskDef of step.tasks) {
              const taskResponse = await createTask({
                variables: {
                  name: taskDef.name,
                  description: taskDef.description,
                  task: taskDef.task_prompt,
                  outputWebhookUrl: '',
                  runSchedule: step.cron_schedule || '',
                  taskType: 'yes_no',
                  taskableType: 'Workflow',
                  taskableUuid: data.createWorkflow.workflow.uuid,
                },
              })

              if (taskResponse.data.createTask.errors.length > 0) {
                console.error('Task creation failed:', taskResponse.data.createTask.errors)
              }
            }
          } else {
            // Create default task if no tasks defined
            const taskResponse = await createTask({
              variables: {
                name: `${step.name} Task`,
                description: step.description,
                task: step.description,
                outputWebhookUrl: '',
                runSchedule: step.cron_schedule || '',
                taskType: 'yes_no',
                taskableType: 'Workflow',
                taskableUuid: data.createWorkflow.workflow.uuid,
              },
            })

            if (taskResponse.data.createTask.errors.length > 0) {
              console.error('Task creation failed:', taskResponse.data.createTask.errors)
            }
          }

          setCreate(true)
          await updateArtifactUuid({
            variables: {
              artifactUuid: artifact.uuid,
              planUuid: step.uuid.value,
              backendUuid: data.createWorkflow.workflow.uuid,
            },
          })
          handleArtifactChange()
          window.toastr.success('Alert created successfully')
        } else {
          const errors = data?.createWorkflow.errors || ['Failed to create alert']
          console.error('Alert creation failed:', errors)
          window.toastr.error(errors.join(', '))
        }
      } else if (isDataSet(step)) {
        // Convert the table definition to the backend format
        let definition = {}
        if (step.backend === 'snowflake') {
          definition = {
            snowflake: {
              schema: step.definition.table_definition.schema || '',
              table: step.definition.table_definition.table_name || '',
              derived_table: step.definition.table_definition.sql || '',
            },
          }
        } else if (step.backend === 'bigquery') {
          definition = {
            bigquery: {
              dataset: step.definition.table_definition.schema || '',
              table: step.definition.table_definition.table_name || '',
              derived_table: step.definition.table_definition.sql || '',
            },
          }
        }

        const { data } = await createDataSet({
          variables: {
            name: step.name,
            description: step.description,
            definition: JSON.stringify(definition),
          },
        })

        if (data?.createDataSet.success && data.createDataSet.dataSet) {
          setCreate(true)
          await updateArtifactUuid({
            variables: {
              artifactUuid: artifact.uuid,
              planUuid: step.uuid.value,
              backendUuid: data.createDataSet.dataSet.uuid,
            },
          })
          handleArtifactChange()
          window.toastr.success('Dataset created successfully')
        } else {
          window.toastr.error(data?.createDataSet.errors?.join(', ') || 'Failed to create dataset')
        }
      } else if (isAgentStep(step)) {
        // Create the agent first
        const { data: agentData } = await createAgent({
          variables: {
            firstName: step.first_name,
            jobTitle: step.job_title || '',
            shortDescription: step.short_description,
            description: step.long_description,
            primer: step.system_instruction,
          },
        })

        if (agentData?.createAgent.success && agentData.createAgent.agent) {
          // Associate datasets with the agent if they exist
          if (step.data_sets) {
            for (const dataSet of step.data_sets) {
              if (dataSet.uuid.type === 'backend') {
                await addDataSetToAgent({
                  variables: {
                    agentUuid: agentData.createAgent.agent.uuid,
                    dataSetUuid: dataSet.uuid.value,
                  },
                })
              }
            }
          }

          setCreate(true)
          await updateArtifactUuid({
            variables: {
              artifactUuid: artifact.uuid,
              planUuid: step.uuid.value,
              backendUuid: agentData.createAgent.agent.uuid,
            },
          })
          handleArtifactChange()
          window.toastr.success('Agent created successfully')
        } else {
          const errors = agentData?.createAgent.errors || ['Failed to create agent']
          console.error('Agent creation failed:', errors)
          window.toastr.error(errors.join(', '))
        }
      }
    } catch (error) {
      console.error('Error creating resource:', error)
      window.toastr.error('Error creating resource')
    } finally {
      setCreateLoading(false)
    }
  }

  if (!isDataSet(step) && !isAgentStep(step) && !isAlertStep(step) && !isAnalysisStep(step)) {
    return null
  }

  // If we have a backend UUID, show the View link instead
  if (step.uuid.type === 'backend') {
    return (
      <div className="mt-4 flex justify-center">
        <Link
          to={`/${
            isDataSet(step)
              ? 'data_set'
              : isAgentStep(step)
              ? 'agent'
              : isAnalysisStep(step)
              ? 'analysis'
              : 'alert'
          }/${step.uuid.value}`}
        >
          <Button className="mt-4 text-xs border border-gray-300 hover:bg-blue-500 hover:text-white px-4 py-1 rounded-md">
            View{' '}
            {isDataSet(step)
              ? 'Dataset'
              : isAgentStep(step)
              ? 'Agent'
              : isAnalysisStep(step)
              ? 'Analysis'
              : 'Alert'}
          </Button>
        </Link>
      </div>
    )
  }

  // Check for plan dependencies
  const hasUnresolvedDependencies = hasPlanDependencies(step)

  return (
    <div className="mt-4 flex justify-center">
      {create && !createLoading && <CircleCheckIcon className="text-green-500" />}
      {!create && !createLoading && (
        <Button
          onClick={handleCreate}
          disabled={hasUnresolvedDependencies}
          className={clsx(
            'mt-4 text-xs border border-gray-300 px-4 py-1 rounded-md',
            hasUnresolvedDependencies
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-blue-500 hover:text-white'
          )}
        >
          {hasUnresolvedDependencies ? 'Dependencies Pending' : 'Create'}
        </Button>
      )}
      {createLoading && <LoaderCircleIcon className="text-gray-500 animate-spin" />}
    </div>
  )
}

const StepAccordion = ({
  step,
  expanded,
  onAccordionChange,
  type,
  children,
  handleArtifactChange,
  artifact,
}: {
  step: Step
  expanded: boolean
  onAccordionChange: (expanded: boolean) => void
  type: StepType
  children: React.ReactNode
  handleArtifactChange: () => void
  artifact: Artifact
}) => (
  <Accordion expanded={expanded} onChange={(e, expanded) => onAccordionChange(expanded)}>
    <AccordionSummary indicator={<ChevronDown />}>
      <StepHeader
        type={type}
        name={
          (() => {
            if (isDataSet(step)) {
              return step.name
            }
            if (isAgentStep(step)) {
              return step.first_name
            }
            return step.name
          })() || ''
        }
      />
    </AccordionSummary>
    <AccordionDetails className="transition-all duration-300 ease-in-out">
      <div className="p-4 border rounded-lg bg-white shadow-sm">
        {children}
        <CreateButton handleArtifactChange={handleArtifactChange} step={step} artifact={artifact} />
      </div>
    </AccordionDetails>
  </Accordion>
)

// Step Components
export const AlertStep = ({
  step: originalStep,
  expanded,
  onAccordionChange,
  allSteps,
  handleArtifactChange,
  artifact,
}: StepProps) => {
  if (!isAlertStep(originalStep)) {
    return null
  }
  const step = originalStep
  const { agentName: alertAgentName } = useAgent(step, allSteps)

  const scheduleText = React.useMemo(() => {
    if (!step.cron_schedule) {
      return 'Manually Launched'
    }
    const parsed = parseCronExpression(step.cron_schedule)
    if (Object.keys(parsed).length === 0) {
      return step.cron_schedule // If we can't parse it, just show the raw string
    }
    const timeOfDay = hours[parsed.hour].label
    if (parsed.timeFrame === 'daily') {
      return `Daily at ${timeOfDay}`
    } else if (parsed.timeFrame === 'weekly') {
      const day = daysOfWeek[parsed.dayOfWeek].name
      return `Every ${day} at ${timeOfDay}`
    } else if (parsed.timeFrame === 'monthly') {
      const day = parsed.dayOfMonth
      return `Monthly on day ${day} at ${timeOfDay}`
    } else if (parsed.timeFrame === 'quarterly') {
      const day = parsed.dayOfQuarter
      return `Quarterly on day ${day} at ${timeOfDay}`
    }
    return step.cron_schedule
  }, [step.cron_schedule])

  return (
    <StepAccordion
      step={step}
      expanded={expanded}
      onAccordionChange={onAccordionChange}
      type="alert"
      handleArtifactChange={handleArtifactChange}
      artifact={artifact}
    >
      <div className={`text-sm text-gray-600 ${contentStyles}`}>{step.description}</div>
      <InfoRow
        icon={<UserCircleIcon className="h-6 w-5 text-gray-400" />}
        content={alertAgentName}
        className="mt-6"
      />
      <InfoRow icon={<Clock10Icon className="h-6 w-5 text-gray-400" />} content={scheduleText} />
      <InfoRow icon={<ListTodoIcon className="h-6 w-5 text-gray-400" />} content="Tasks" />

      {step.tasks && step.tasks.length > 0 && (
        <div className="mt-4 ml-4 text-xs">
          <div className="space-y-2">
            {step.tasks.map((task: Task, index: number) => (
              <div key={index} className="flex flex-col space-y-1 pl-4 border-l-2 border-blue-200">
                <div className="font-bold">{task.name}</div>
                <div className={`font-semibold text-gray-500 ${contentStyles}`}>
                  {task.description}
                </div>
                <div className="text-xs text-gray-500">{task.task_prompt}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </StepAccordion>
  )
}

export const AnalysisStep = ({
  step: originalStep,
  expanded,
  onAccordionChange,
  allSteps,
  handleArtifactChange,
  artifact,
}: StepProps) => {
  if (!isAnalysisStep(originalStep)) {
    return null
  }
  const step = originalStep
  const { agentName } = useAgent(step, allSteps)

  return (
    <StepAccordion
      step={step}
      expanded={expanded}
      onAccordionChange={onAccordionChange}
      type="analysis"
      handleArtifactChange={handleArtifactChange}
      artifact={artifact}
    >
      <InfoRow icon={<UserCircleIcon className="h-6 w-5 text-gray-400" />} content={agentName} />
      <InfoRow
        icon={<RectangleHorizontal className="h-6 w-5 text-gray-400" />}
        content={<div className={`text-sm text-gray-600 ${contentStyles}`}>{step.description}</div>}
      />
      {step.synthesis && (
        <InfoRow
          icon={<TextIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Synthesis</div>
              <div className={`text-sm text-gray-500 ${contentStyles}`}>{step.synthesis}</div>
            </div>
          }
        />
      )}
      {step.tasks && step.tasks.length > 0 && (
        <InfoRow
          icon={<ListTodoIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Tasks</div>
              <div className="mt-2 space-y-4">
                {step.tasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex flex-col space-y-1 pl-4 border-l-2 border-blue-200"
                  >
                    <div className="font-bold text-sm">{task.name}</div>
                    <div className={`text-sm text-gray-500 ${contentStyles}`}>
                      {task.description}
                    </div>
                    <div className="text-xs text-gray-500">{task.task_prompt}</div>
                  </div>
                ))}
              </div>
            </div>
          }
        />
      )}
    </StepAccordion>
  )
}

export const AgentStep = ({
  step: originalStep,
  expanded,
  onAccordionChange,
  allSteps,
  handleArtifactChange,
  artifact,
}: StepProps) => {
  if (!isAgentStep(originalStep)) {
    return null
  }
  const step = originalStep
  const dataSetNames = useDataSets(step.data_sets, allSteps)

  return (
    <StepAccordion
      step={step}
      expanded={expanded}
      onAccordionChange={onAccordionChange}
      type="agent"
      handleArtifactChange={handleArtifactChange}
      artifact={artifact}
    >
      <InfoRow
        icon={<UserCircleIcon className="h-6 w-5 text-gray-400" />}
        content={
          <div className="font-medium">
            {step.first_name} ({step.job_title})
          </div>
        }
      />
      <InfoRow
        icon={<TextIcon className="h-6 w-5 text-gray-400" />}
        content={
          <div>
            <div className="font-medium">Description</div>
            <div className="flex flex-col space-y-2 text-sm text-gray-500 ">
              <div>
                <div className="font-medium">Short</div>
                <div>{step.short_description}</div>
              </div>
              <div>
                <div className="font-medium">Long</div>
                <div>{step.long_description}</div>
              </div>
            </div>
          </div>
        }
      />
      {step.system_instruction && (
        <InfoRow
          icon={<WrenchIcon className="h-6 w-5 mt-1 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Instructions</div>
              <div className="text-sm text-gray-500">{step.system_instruction}</div>
            </div>
          }
        />
      )}
      {dataSetNames.length > 0 && (
        <InfoRow
          icon={<TableIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div className="flex flex-col space-y-2">
              <div className="font-medium">Data Sets:</div>
              <div className="space-y-2">
                {dataSetNames.map((name: string, index: number) => (
                  <div key={index} className="text-sm">
                    {name}
                  </div>
                ))}
              </div>
            </div>
          }
        />
      )}
    </StepAccordion>
  )
}

const DataSetTableSchema = ({ schema, table_name }: { schema?: string; table_name: string }) => (
  <>
    {schema && (
      <InfoRow icon={<FolderOpenIcon className="h-6 w-5 text-gray-400" />} content={schema} />
    )}
    <InfoRow icon={<TableIcon className="h-6 w-5 text-gray-400" />} content={table_name} />
  </>
)

const DataSetViewSchema = ({ sql }: { sql: string }) => (
  <InfoRow
    icon={<CodeIcon className="h-6 w-5 text-gray-400" />}
    content={<pre className={`mt-1 text-xs ${contentStyles}`}>{sql}</pre>}
  />
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const DataSetStep = ({
  step,
  artifact,
  expanded,
  onAccordionChange,
  handleArtifactChange,
}: StepProps) => {
  if (!isDataSet(step)) {
    return null
  }

  return (
    <StepAccordion
      step={step}
      expanded={expanded}
      onAccordionChange={onAccordionChange}
      type="data_set"
      handleArtifactChange={handleArtifactChange}
      artifact={artifact}
    >
      <div className={`text-sm text-gray-600 ${contentStyles}`}>{step.description}</div>
      <InfoRow
        icon={<DatabaseIcon className="h-6 w-5 text-gray-400" />}
        content={step.backend ? BACKEND_LABELS[step.backend] : 'Unknown'}
        className="mt-6"
      />

      {step.definition && (
        <>
          {step.definition.type === 'snowflake' && (
            <>
              {step.definition.table_definition.type === 'table' ? (
                <DataSetTableSchema
                  schema={step.definition.table_definition.schema}
                  table_name={step.definition.table_definition.table_name || ''}
                />
              ) : (
                <DataSetViewSchema sql={step.definition.table_definition.sql || ''} />
              )}
            </>
          )}
          {step.definition.type === 'bigquery' && (
            <>
              {step.definition.table_definition.type === 'table' ? (
                <DataSetTableSchema
                  schema={step.definition.table_definition.schema}
                  table_name={step.definition.table_definition.table_name || ''}
                />
              ) : (
                <DataSetViewSchema sql={step.definition.table_definition.sql || ''} />
              )}
            </>
          )}
        </>
      )}
    </StepAccordion>
  )
}

interface Artifact {
  uuid: string
  name: string
  description: string
  artifactType: string
  createdAt: string
  updatedAt: string
  content: Step[]
}

export const CreatePlan = ({
  artifact,
  handleArtifactChange,
}: {
  artifact: Artifact
  handleArtifactChange: () => void
}) => {
  const steps = artifact.content as Step[]
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedSteps, setExpandedSteps] = useState<{ [key: string]: boolean }>({})

  // Initialize expanded state for all steps
  useEffect(() => {
    const initialState = steps.reduce((acc: { [key: string]: boolean }, step: Step) => {
      acc[getStepId(step.uuid)] = true
      return acc
    }, {})
    setExpandedSteps(initialState)
  }, [steps])

  const toggleExpansion = () => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    const newExpandedSteps = Object.keys(expandedSteps).reduce(
      (acc: { [key: string]: boolean }, key) => {
        acc[key] = newExpandedState
        return acc
      },
      {}
    )
    setExpandedSteps(newExpandedSteps)
  }

  const handleStepChange = (uuid: UuidReference, expanded: boolean) => {
    const stepId = getStepId(uuid)
    setExpandedSteps((prev) => ({
      ...prev,
      [stepId]: expanded,
    }))
    // Update isExpanded based on whether all steps are expanded or not
    setIsExpanded(Object.values({ ...expandedSteps, [stepId]: expanded }).every((value) => value))
  }

  return (
    <div>
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <div className="text-lg font-bold line-clamp-1">{artifact.name}</div>
          <div
            className="flex flex-row border rounded-md p-1 cursor-pointer hover:bg-gray-100"
            onClick={toggleExpansion}
          >
            {isExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </div>
        </div>
        <div className="text-sm text-gray-500">{artifact.description}</div>
      </div>
      <div className="mt-8">
        <AccordionGroup transition="0.2s" disableDivider>
          {artifact.content.map((step, index) => {
            if (!isStep(step)) {
              return null
            }
            const expanded = expandedSteps[getStepId(step.uuid)] ?? true
            return (
              <div key={index} className="flex-grow">
                {step.type === 'alert' && (
                  <AlertStep
                    step={step}
                    expanded={expanded}
                    onAccordionChange={(expanded) => handleStepChange(step.uuid, expanded)}
                    allSteps={artifact.content}
                    handleArtifactChange={handleArtifactChange}
                    artifact={artifact}
                  />
                )}
                {step.type === 'analysis' && (
                  <AnalysisStep
                    step={step}
                    expanded={expanded}
                    onAccordionChange={(expanded) => handleStepChange(step.uuid, expanded)}
                    allSteps={artifact.content}
                    handleArtifactChange={handleArtifactChange}
                    artifact={artifact}
                  />
                )}
                {step.type === 'agent' && (
                  <AgentStep
                    step={step}
                    expanded={expanded}
                    onAccordionChange={(expanded) => handleStepChange(step.uuid, expanded)}
                    allSteps={artifact.content}
                    handleArtifactChange={handleArtifactChange}
                    artifact={artifact}
                  />
                )}
                {step.type === 'data_set' && (
                  <DataSetStep
                    step={step}
                    expanded={expanded}
                    onAccordionChange={(expanded) => handleStepChange(step.uuid, expanded)}
                    allSteps={artifact.content}
                    handleArtifactChange={handleArtifactChange}
                    artifact={artifact}
                  />
                )}
                {step.type === 'email_update' && (
                  <EmailUpdateStep
                    step={step}
                    expanded={expanded}
                    onAccordionChange={(expanded) => handleStepChange(step.uuid, expanded)}
                    allSteps={artifact.content}
                    handleArtifactChange={handleArtifactChange}
                    artifact={artifact}
                  />
                )}
              </div>
            )
          })}
        </AccordionGroup>
      </div>
    </div>
  )
}

export const CreateList: React.FC<CreateListProps> = ({ artifacts, handleArtifactChange }) => {
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (artifacts.length > 0) {
      setActiveStep(artifacts.length - 1)
    }
  }, [artifacts])

  if (!artifacts || artifacts.length === 0) {
    return <></>
  }

  const handleNext = () => {
    if (activeStep < artifacts.length - 1) {
      setActiveStep(activeStep + 1)
    }
  }

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1)
    }
  }

  return (
    <div className="relative flex flex-col">
      <div className="absolute inset-0 w-full flex flex-row h-10 items-center px-1 justify-end border-b border-gray-200 bg-blue-50 space-x-4">
        <div className="text-xs text-gray-500">
          {activeStep + 1} of {artifacts.length} plans
        </div>
        <div className="flex flex-row gap-2">
          <ChevronLeft
            className={clsx('text-gray-500', {
              'opacity-50': activeStep === 0,
              'cursor-pointer': activeStep > 0,
            })}
            onClick={handlePrevious}
          />
          <ChevronRight
            className={clsx('text-gray-500', {
              'opacity-50': activeStep === artifacts.length - 1,
              'cursor-pointer': activeStep < artifacts.length - 1,
            })}
            onClick={handleNext}
          />
        </div>
      </div>
      <div className="flex-grow mt-10 px-5 py-2 overflow-y-auto">
        <CreatePlan artifact={artifacts[activeStep]} handleArtifactChange={handleArtifactChange} />
      </div>
    </div>
  )
}

const dummyArtifacts: GraphQLArtifact[] = [
  {
    uuid: '1',
    name: 'Test Plan 1: Basic Monitoring Setup',
    description: 'A comprehensive monitoring setup with alerts, analysis, and data collection',
    artifactType: 'create_list',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    content: [
      {
        type: 'alert',
        name: 'High CPU Usage Alert',
        description: 'Monitor system CPU usage and alert when it exceeds thresholds',
        uuid: toUuidReference('alert-1', 'plan'),
        agent_uuid: toUuidReference('agent-1', 'backend'),
        tasks: [
          {
            name: 'Check CPU Usage',
            description: 'Verify if CPU usage is within acceptable limits',
            task_prompt:
              'Check if CPU usage is below 80%. Return PASS if CPU usage is below 80%, FAIL otherwise.',
            uuid: 'task-1',
            type: 'pass_fail',
          },
        ],
      },
      {
        type: 'analysis',
        name: 'System Performance Analysis',
        description: 'Analyze overall system performance metrics',
        synthesis:
          'Based on collected metrics, the system is performing within expected parameters',
        uuid: toUuidReference('analysis-1', 'plan'),
        agent_uuid: toUuidReference('agent-1', 'backend'),
      },
      {
        type: 'agent',
        first_name: 'MonitorBot',
        short_description: 'System monitoring specialist',
        long_description:
          'An AI agent specialized in monitoring system metrics and analyzing performance patterns',
        system_instruction:
          'You are a monitoring specialist. Focus on system metrics and performance analysis.',
        uuid: toUuidReference('agent-1', 'plan'),
      },
      {
        type: 'data_set',
        name: 'Performance Metrics Dataset',
        description: 'Collection of system performance metrics',
        uuid: toUuidReference('dataset-1', 'plan'),
        backend: 'snowflake' as Backend,
        definition: {
          type: 'snowflake',
          table_definition: {
            type: 'view',
            sql: 'SELECT timestamp, cpu_usage, memory_usage FROM system_metrics WHERE date = CURRENT_DATE()',
          },
        },
      },
    ],
  },
  {
    uuid: '2',
    name: 'Test Plan 2: Data Analysis Pipeline',
    description: 'A data analysis pipeline with BigQuery integration',
    artifactType: 'create_list',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    content: [
      {
        type: 'data_set',
        name: 'User Analytics Dataset',
        description: 'User behavior analytics data',
        uuid: toUuidReference('dataset-2', 'plan'),
        backend: 'bigquery' as Backend,
        definition: {
          type: 'bigquery',
          table_definition: {
            type: 'view',
            sql: 'SELECT user_id, event_type, timestamp FROM user_events WHERE date >= DATE_SUB(CURRENT_DATE(), INTERVAL 7 DAY)',
          },
        },
      },
      {
        type: 'analysis',
        name: 'User Engagement Analysis',
        description: 'Analyze user engagement patterns',
        synthesis: 'User engagement shows a 15% increase in daily active users',
        uuid: toUuidReference('analysis-2', 'plan'),
        agent_uuid: toUuidReference('agent-2', 'backend'),
      },
      {
        type: 'agent',
        first_name: 'AnalyticsBot',
        short_description: 'Data analysis specialist',
        long_description:
          'An AI agent specialized in analyzing user behavior patterns and generating insights',
        system_instruction:
          'You are a data analyst. Focus on user behavior patterns and engagement metrics.',
        uuid: toUuidReference('agent-2', 'plan'),
      },
    ],
  },
]

export const DummyCreateList: React.FC = () => {
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const threadUuid = searchParams.get('thread_uuid')
  const isTestMode = searchParams.get('test') !== null

  const { data } = useQuery<ArtifactsQuery, ArtifactsQueryVariables>(GET_ARTIFACTS, {
    variables: {
      messageThreadUuid: threadUuid,
    },
    skip: isTestMode,
  })

  const artifacts = isTestMode ? dummyArtifacts : data?.artifacts || []

  return (
    <div className="absolute inset-0 flex flex-row h-full">
      <div className="flex flex-col h-full w-[50%] bg-gray-200 shadow-inner overflow-y-auto">
        Dummy chat area
      </div>
      <div className="w-[50%] overflow-y-auto">
        <CreateList
          artifacts={artifacts}
          handleArtifactChange={() => {
            console.log('handleArtifactChange')
          }}
        />
      </div>
    </div>
  )
}

export const EmailUpdateStep = ({
  step: originalStep,
  expanded,
  onAccordionChange,
  allSteps,
  handleArtifactChange,
  artifact,
}: StepProps) => {
  if (!isEmailUpdateStep(originalStep)) {
    return null
  }
  const step = originalStep
  const { agentName } = useAgent(step, allSteps)

  return (
    <StepAccordion
      step={step}
      expanded={expanded}
      onAccordionChange={onAccordionChange}
      type="email_update"
      handleArtifactChange={handleArtifactChange}
      artifact={artifact}
    >
      <InfoRow
        icon={<UserCircleIcon className="h-6 w-5 text-gray-400" />}
        content={agentName}
        className="mt-6"
      />
      <InfoRow
        icon={<RectangleHorizontal className="h-6 w-5 text-gray-400" />}
        content={<div className={`text-sm text-gray-600 ${contentStyles}`}>{step.description}</div>}
      />
      {step.synthesis && (
        <InfoRow
          icon={<TextIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Email Content</div>
              <div className={`text-sm text-gray-500 ${contentStyles}`}>{step.synthesis}</div>
            </div>
          }
        />
      )}
      {step.tasks && step.tasks.length > 0 && (
        <InfoRow
          icon={<ListTodoIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Tasks</div>
              <div className="mt-2 space-y-4">
                {step.tasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex flex-col space-y-1 pl-4 border-l-2 border-blue-200"
                  >
                    <div className="font-bold text-sm">{task.name}</div>
                    <div className={`text-sm text-gray-500 ${contentStyles}`}>
                      {task.description}
                    </div>
                    <div className="text-xs text-gray-500">{task.task_prompt}</div>
                  </div>
                ))}
              </div>
            </div>
          }
        />
      )}
      {step.attachments && step.attachments.length > 0 && (
        <InfoRow
          icon={<PaperclipIcon className="h-6 w-5 text-gray-400" />}
          content={
            <div>
              <div className="font-medium">Attachments</div>
              <div className="mt-2 space-y-4">
                {step.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex flex-col space-y-1 pl-4 border-l-2 border-blue-200"
                  >
                    <div className="font-bold text-sm">{attachment.file_type}</div>
                    <div className={`text-sm text-gray-500 ${contentStyles}`}>
                      {attachment.prompt}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }
        />
      )}
    </StepAccordion>
  )
}
